/* The course, as the site sees it.
 *
 * The content itself lives in `content/cs140/` — Markdown and YAML, owned by
 * the curriculum, not by this app. `node content/build.mjs` compiles it to
 * generated/cs140.json and this module adapts that into the view model the
 * components want.
 *
 * Keeping the adapter here is the point of the split: the content tree is free
 * to describe a course however a course is best described (weeks, outcomes,
 * checkpoints) without knowing that a `<CourseHome>` exists, and the components
 * keep a stable shape without knowing where content came from.
 */

import data from '../generated/cs140.json';

const { course: raw, bodies } = data;

const outcomeTitle = n => (raw.outcomes.find(o => o.n === n) || {}).title || `Outcome ${n}`;

import { PUBLISHED_THROUGH_MODULE } from '../config.js';

/* Units are the course's modules (D-038) — Orientation, the teaching modules,
   the final checkpoint. The site never labels one with a week: pacing is
   cohort-bound, and cohort-bound is Canvas's (D-001). Module start dates are
   Nick's (D-058); the build turns them into weeks of the term, used here for
   one thing only, the release gate below. No date is ever shown. Outcome
   numbers stay off the page too — planning information, which outline.md
   carries for the instructor. */
const cal = raw.calendar || {};

/* The release gate (D-059). Every module up to and including
   PUBLISHED_THROUGH_MODULE, in teaching order — whole modules, never half of
   one; dates play no part. Removed
   here, at the adapter, so every consumer follows without knowing about the
   flag: the course home, the syllabus, the breadcrumb's unit menu, prev/next,
   and the lesson lookup in App — a direct URL to an unpublished lesson lands on
   the not-found page rather than rendering something unreviewed. */
const ORDER = [...raw.modules.map(m => m.id), 'final'];
if (PUBLISHED_THROUGH_MODULE != null && !ORDER.includes(PUBLISHED_THROUGH_MODULE))
  throw new Error(`PUBLISHED_THROUGH_MODULE: no module "${PUBLISHED_THROUGH_MODULE}" in course.yml`);
const published = id => PUBLISHED_THROUGH_MODULE == null || ORDER.indexOf(id) <= ORDER.indexOf(PUBLISHED_THROUGH_MODULE);

/* A module's assignments follow its lessons, in order, once written (D-039,
   D-046) — a planned one is not shown: the site never promises what does not exist. */
const withAssignment = m => [
  ...m.lessons,
  ...(m.assignments || []).filter(a => bodies[a.id])
    .map(a => ({ id: a.id, title: bodies[a.id].title, kind: 'assignment' })),
];

const unit = (m, i) => ({
  id: m.id,
  starts: m.starts,
  num: String(i + 1),
  name: m.name,
  meta: `Module ${i + 1}`,
  blurb: m.blurb,
  outcomes: [].concat(m.outcomes || []),
  checkpoint: null,
  lessons: withAssignment(m),
});

/* Orientation is always published, first, and labelled for what it is. It
   carries no outcomes — nothing in it is assessed. */
const orientation = raw.orientation ? [{
  id: 'start-here', num: '0', name: raw.orientation.name, meta: null,   // its name says it; a label would repeat it
  starts: raw.orientation.starts,
  blurb: raw.orientation.blurb, outcomes: [], checkpoint: null, lessons: raw.orientation.lessons,
}] : [];

const finalUnit = raw.final && published('final') ? [{
  id: 'final', num: '', name: 'Final checkpoint', meta: 'Every outcome',
  starts: raw.final.starts,
  blurb: 'Every outcome, re-tested.',
  outcomes: raw.outcomes.map(o => o.n), checkpoint: null, lessons: withAssignment(raw.final),
}] : [];

export const course = {
  ...raw,
  units: [
    ...orientation,
    ...raw.modules.map(unit).filter(u => published(u.id)),
    ...finalUnit,
  ],
};

/** Modules in the course, published or not — "3 of 11 modules" needs both. */
export const totalModules = raw.modules.length;

/** How many weeks the term has, by the course's dates — the term panel's
    denominator, whatever is published. */
export const totalWeeks = raw.weeks.length;

export const outcomes = raw.outcomes;
export { outcomeTitle };

/** Lesson body, or null when the lesson is a stub. */
export const getLesson = id => bodies[id] || null;

export const unitName = u => u.name;

export const allLessons = (units = course.units) =>
  units.flatMap(u => u.lessons.map(l => ({ ...l, unit: u })));

export const lessonCount = () => allLessons().length;

export const writtenCount = () => allLessons().filter(l => bodies[l.id]).length;

export const findLesson = (id, units = course.units) => allLessons(units).find(l => l.id === id);

export const neighbours = (id, units = course.units) => {
  const all = allLessons(units);
  const i = all.findIndex(l => l.id === id);
  return { prev: i > 0 ? all[i - 1] : null, next: i >= 0 && i < all.length - 1 ? all[i + 1] : null };
};
